import { BHG_ROUTES } from "@constructorfleet/bhg-product-core";
import {
  Button,
  CTABand,
  CompareGrid,
  Container,
  DashboardQuadrantPreview,
  DisclaimerCard,
  IconAlert,
  IconBell,
  IconCalendar,
  IconMessage,
  PhonePairPreview,
  PriceLockup,
  ReliabilityGrid,
  SectionHead,
  Stepper,
} from "@constructorfleet/ui/design-system";

/**
 * Product FAQ. Single source of truth: rendered both as the visible on-page
 * section AND as FAQPage structured data below, so the two can never drift —
 * Google requires the schema's Q&A text to match what's visible on the page.
 *
 * Questions are phrased as the long-tail queries patients actually search
 * ("forgot to call my methadone clinic", "what's a bottle check") so the page
 * can rank for them. "methadone" and "Behavioral Health Group (BHG)" appear
 * descriptively — we augment the clinic's call-in line, we are not affiliated.
 */
const PRODUCT_FAQ: { q: string; a: string }[] = [
  {
    q: "What does Check-in Assist do?",
    a: "Check-in Assist makes your daily methadone clinic call-in for you, listens to the automated line, and texts you exactly what your clinic needs that day — a Bottle Check, a Drug Screen, both, or nothing scheduled — and whether it is due today or tomorrow. It works alongside your Behavioral Health Group (BHG) clinic line.",
  },
  {
    q: "What happens if I forget to call my methadone clinic?",
    a: "That is the problem Check-in Assist solves. It places the daily call-in automatically so you do not have to remember, and it tells you the result. A single missed call-in can disrupt your treatment or move you to daily in-person visits, so the automatic call is there to keep that from slipping through the cracks.",
  },
  {
    q: "How will I know if I have a Bottle Check or a Drug Screen?",
    a: "After the daily call, you get a text that names exactly what is required — a Bottle Check, a Drug Screen, both on the same day, or nothing scheduled — along with the day it applies. If nothing is required, you will know you can skip the trip.",
  },
  {
    q: "Is ConstructorFleet affiliated with Behavioral Health Group (BHG)?",
    a: "No. ConstructorFleet is not affiliated with or endorsed by BHG or any clinic, provider, or treatment program, and does not provide medical or healthcare services. Check-in Assist simply automates the daily call-in on your behalf. You are still responsible for following through on any required action.",
  },
  {
    q: "What if the call fails or the clinic line is down?",
    a: "Every attempt is logged whether it succeeds or not, and you are notified either way. If a call cannot get through, you will know in time to call in yourself.",
  },
];

function ProductFaqJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: PRODUCT_FAQ.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  // JSON-LD is valid anywhere in the document; the prerender step snapshots the
  // full DOM, so this body <script> is baked into the static HTML for crawlers.
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function BhgCallProductPage() {
  return (
    <main aria-label="BHG Check-in Assist">
      {/* ── 1. Product hero ──────────────────────────────────── */}
      <section className="phero">
        <Container>
          <div className="phero-grid">
            <div>
              <span className="tag">BHG Check-in Assist</span>
              <h1 className="display" style={{ marginTop: 20 }}>
                Your daily call-in, handled automatically.
              </h1>
              <p className="lede">
                Check-in Assist calls the BHG medication line for you every day, gets your result,
                and sends it to you — before your day gets away from you.
              </p>

              <PriceLockup
                amount="$15"
                period="/mo"
                caption="Flat rate · No hidden fees · Cancel anytime"
              />

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                <Button as="a" href={`/${BHG_ROUTES.SIGNUP}`} variant="brand" size="lg">
                  Get Started
                </Button>
                <Button as="a" href={`/${BHG_ROUTES.DEMO}`} variant="secondary" size="lg">
                  Explore Demo
                </Button>
              </div>
            </div>

            <div className="phero-visual">
              <DashboardQuadrantPreview>
                <img
                  src="/bhg-checkin-snapshot.png"
                  alt="BHG Check-in Assist dashboard snapshot"
                  style={{ display: "block", width: "75%", height: "auto" }}
                />
              </DashboardQuadrantPreview>
            </div>
          </div>
        </Container>
      </section>

      {/* ── 2. Disclaimer ────────────────────────────────────── */}
      <section className="disc">
        <Container>
          <DisclaimerCard icon={<IconAlert />}>
            ConstructorFleet is not affiliated with or endorsed by BHG or any clinic, provider, or
            treatment program. ConstructorFleet does not provide medical, clinical, or healthcare
            services. All data is collected at your direction and is not received from or maintained
            on behalf of any healthcare provider. This tool automates the daily IVR call on your
            behalf. <b>No outcome guarantees</b> — you are still responsible for following through
            on any required action.
          </DisclaimerCard>
        </Container>
      </section>

      {/* ── 3. What you get vs. what's at risk ──────────────── */}
      <section className="compare">
        <Container>
          <SectionHead
            align="center"
            kicker="What's at stake"
            title="What you get vs. what's at risk."
            brand
          />
          <CompareGrid
            good={{
              label: "With",
              title: "What you actually get",
              items: [
                {
                  title: "Automated daily calls",
                  desc: "The medication line call happens for you — no need to remember or dial in yourself.",
                },
                {
                  title: "Your result by text",
                  desc: "You get a clear message telling you whether you need to report and what day applies.",
                },
                {
                  title: "Transcripts and audio you can download",
                  desc: "Every call is recorded and timestamped. Pull it up whenever you need proof.",
                },
              ],
            }}
            bad={{
              label: "Without",
              title: "What's at stake without it",
              items: [
                {
                  title: "Missing a drug screen or Bottle Check",
                  desc: "A single missed call can disrupt your treatment and move you to daily visits.",
                },
                {
                  title: "Relying on memory or timing luck",
                  desc: "Life gets busy. The call window is narrow, and there's no second chance if you miss it.",
                },
                {
                  title: "No record of what happened",
                  desc: "If there's ever a dispute about your check-in, you have nothing to point to.",
                },
              ],
            }}
          />
        </Container>
      </section>

      {/* ── 4. How it works ──────────────────────────────────── */}
      <section className="how">
        <Container>
          <SectionHead
            align="center"
            kicker="How it works"
            title="Three steps. Set once, runs forever."
            brand
          />
          <Stepper
            steps={[
              {
                title: "Enter your details",
                desc: "Add your patient ID and clinic info once during setup. That's the only time you'll need to enter it.",
              },
              {
                title: "We call the line",
                desc: "Every day, Check-in Assist calls the BHG medication line automatically and captures the result.",
              },
              {
                title: "Get your result by text",
                desc: "You choose your delivery time. The update lands when it's actually useful — plus a full record in your dashboard.",
              },
            ]}
          />
        </Container>
      </section>

      {/* ── 5. Records ───────────────────────────────────────── */}
      <section className="records">
        <Container>
          <SectionHead
            align="center"
            kicker="Your records"
            title="Ready when you need them."
            lede="Every call is logged with a timestamp, transcript, and audio file. Today's result, your full call history, and downloadable transcripts — all in one place."
            brand
          />
          <PhonePairPreview deepScrollPx={0} includePhoneFrame={false}>
            <img
              src="/bhg-checkin-phone-frame1.png"
              alt="BHG Check-in Assist phone preview one"
              style={{ display: "block", width: "100%", height: "100%" }}
            />
            <img
              src="/bhg-checkin-phone-frame2.png"
              alt="BHG Check-in Assist phone preview two"
              style={{ display: "block", width: "100%", height: "100%" }}
            />
          </PhonePairPreview>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button as="a" href={`/${BHG_ROUTES.DEMO}`} variant="primary" size="md">
              View demo dashboard
            </Button>
          </div>
        </Container>
      </section>

      {/* ── 6. Reliability (dark) ────────────────────────────── */}
      <section className="baddays">
        <Container>
          <SectionHead
            align="center"
            kicker="Reliability"
            title="What happens if something goes wrong"
            lede="Automation only matters if it handles the bad days too. Here's how Check-in Assist deals with the situations that trip people up."
          />
          <ReliabilityGrid
            items={[
              {
                icon: <IconBell />,
                title: "You're notified before the call",
                desc: "You get a heads-up before the call is placed so you know it's happening. No guessing, no surprises.",
              },
              {
                icon: <IconMessage />,
                title: "You're notified after the call",
                desc: "As soon as the call completes, you get your result. If the call fails, you'll know in time to call in yourself.",
              },
              {
                icon: <IconCalendar />,
                title: "System is down",
                desc: "Every call — successful or not — is timestamped and logged. If there's ever a question about what happened, the record exists.",
              },
            ]}
          />
        </Container>
      </section>

      {/* ── 7. FAQ ───────────────────────────────────────────── */}
      <section className="faq">
        <Container>
          <div className="faq-inner">
            <h2>Questions patients ask</h2>
            {PRODUCT_FAQ.map((item) => (
              <div className="qa" key={item.q}>
                <b>{item.q}</b>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </Container>
        <ProductFaqJsonLd />
      </section>

      {/* ── 8. Final CTA ─────────────────────────────────────── */}
      <section className="cta-final">
        <Container>
          <CTABand
            title="Ready to stop worrying about the daily call?"
            ctas={[
              { label: "Get Started", href: `/${BHG_ROUTES.SIGNUP}`, variant: "brand" },
              { label: "Explore Demo", href: `/${BHG_ROUTES.DEMO}`, variant: "secondary" },
            ]}
          />
        </Container>
      </section>
    </main>
  );
}
